import { useMemo } from 'react';
import type { Customer, LogEntry, LogType, Quote, QuoteStatus, User } from '../types';
import { fmtCcy } from '../domain';
import { QuoteRowActions, QuoteStatusBadge } from './Quotes';

export type ActivityFilter = 'all' | LogType | 'quote';

interface Props {
  user: User;
  customers: Customer[];
  visibleCustomers: Customer[];
  logs: LogEntry[];
  quotes: Quote[];
  filterCustomer: string | null;
  filterAM: string | null;
  filterType: ActivityFilter;
  searchQ: string;
  view: 'all' | 'today';
  onOpenLog: (type: LogType) => void;
  onOpenQuote: () => void;
  onSetFilterType: (t: ActivityFilter) => void;
  onSetSearch: (q: string) => void;
  onEditLog: (l: LogEntry) => void;
  onDeleteLog: (id: string) => void;
  onEditQuote: (q: Quote) => void;
  onSetQuoteStatus: (q: Quote, status: QuoteStatus) => void;
  onDeleteQuote: (q: Quote) => void;
}

interface ActivityRow {
  kind: 'log' | 'quote';
  ts: number;
  log?: LogEntry;
  quote?: Quote;
  customerId: string;
  customerName: string;
  user: string;
  searchText: string;
}

const LOG_ICON: Record<LogEntry['type'], string> = { call: '📞', email: '✉️', note: '📝' };

function formatTs(ts: number): string {
  const dt = new Date(ts);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function Dashboard(props: Props) {
  const {
    user, customers, visibleCustomers, logs, quotes,
    filterCustomer, filterAM, filterType, searchQ, view,
    onOpenLog, onOpenQuote, onSetFilterType, onSetSearch,
    onEditLog, onDeleteLog,
    onEditQuote, onSetQuoteStatus, onDeleteQuote
  } = props;

  // Apply AM/own scope to logs and quotes.
  const visibleIds = useMemo(() => new Set(visibleCustomers.map(c => c.id)), [visibleCustomers]);

  const scopedLogs = useMemo(() => {
    if (user.role === 'admin' && !filterAM) return logs;
    return logs.filter(l => visibleIds.has(l.customerId));
  }, [logs, user.role, filterAM, visibleIds]);

  const scopedQuotes = useMemo(() => {
    if (user.role === 'admin' && !filterAM) return quotes;
    return quotes.filter(q => visibleIds.has(q.customerId));
  }, [quotes, user.role, filterAM, visibleIds]);

  // Merged activity rows.
  const allRows = useMemo<ActivityRow[]>(() => {
    const lr: ActivityRow[] = scopedLogs.map(l => ({
      kind: 'log', ts: l.ts, log: l,
      customerId: l.customerId, customerName: l.customerName,
      user: l.user, searchText: `${l.notes} ${l.customerName ?? ''}`
    }));
    const qr: ActivityRow[] = scopedQuotes.map(q => ({
      kind: 'quote', ts: q.createdTs, quote: q,
      customerId: q.customerId, customerName: q.customerName,
      user: q.user, searchText: `${q.clarityRef} ${q.notes} ${q.customerName}`
    }));
    return [...lr, ...qr];
  }, [scopedLogs, scopedQuotes]);

  const filtered = useMemo(() => {
    let r = allRows;
    if (filterCustomer) r = r.filter(x => x.customerId === filterCustomer);
    if (view === 'today') {
      const today = new Date().toDateString();
      r = r.filter(x => new Date(x.ts).toDateString() === today);
    }
    if (filterType === 'quote') r = r.filter(x => x.kind === 'quote');
    else if (filterType !== 'all') r = r.filter(x => x.kind === 'log' && x.log!.type === filterType);
    if (searchQ) {
      const q = searchQ.toLowerCase();
      r = r.filter(x => x.searchText.toLowerCase().includes(q));
    }
    return [...r].sort((a, b) => b.ts - a.ts);
  }, [allRows, filterCustomer, view, filterType, searchQ]);

  // Stats today (across both logs and quotes).
  const statBase = useMemo(() => (
    filterCustomer ? allRows.filter(r => r.customerId === filterCustomer) : allRows
  ), [allRows, filterCustomer]);

  const todayRows = useMemo(() => {
    const today = new Date().toDateString();
    return statBase.filter(r => new Date(r.ts).toDateString() === today);
  }, [statBase]);

  const custLabel = filterCustomer
    ? (customers.find(c => c.id === filterCustomer)?.name ?? 'Customer')
    : (filterAM ?? 'All Customers');

  return (
    <>
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Calls Today</div>
          <div className="stat-val calls">{todayRows.filter(r => r.kind === 'log' && r.log!.type === 'call').length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Emails Today</div>
          <div className="stat-val emails">{todayRows.filter(r => r.kind === 'log' && r.log!.type === 'email').length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Quotes Today</div>
          <div className="stat-val notes">{todayRows.filter(r => r.kind === 'quote').length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Entries</div>
          <div className="stat-val total">{statBase.length}</div>
        </div>
      </div>

      <div className="log-header">
        <div>
          <div className="log-title">Activity Log <span>{custLabel}</span></div>
        </div>
        <div className="log-actions">
          <button className="action-btn btn-call" onClick={() => onOpenLog('call')}>📞 Call</button>
          <button className="action-btn btn-email" onClick={() => onOpenLog('email')}>✉️ Email</button>
          <button className="action-btn btn-note" onClick={() => onOpenLog('note')}>📝 Note</button>
          <button className="action-btn btn-email" onClick={onOpenQuote}>💬 Quote</button>
        </div>
      </div>

      <div className="filter-bar">
        <button className={`filter-btn ${filterType === 'all' ? 'active' : ''}`} onClick={() => onSetFilterType('all')}>All</button>
        <button className={`filter-btn ${filterType === 'call' ? 'active' : ''}`} onClick={() => onSetFilterType('call')}>📞 Calls</button>
        <button className={`filter-btn ${filterType === 'email' ? 'active' : ''}`} onClick={() => onSetFilterType('email')}>✉️ Emails</button>
        <button className={`filter-btn ${filterType === 'note' ? 'active' : ''}`} onClick={() => onSetFilterType('note')}>📝 Notes</button>
        <button className={`filter-btn ${filterType === 'quote' ? 'active' : ''}`} onClick={() => onSetFilterType('quote')}>💬 Quotes</button>
        <input
          className="search-input"
          type="text"
          placeholder="Search…"
          value={searchQ}
          onChange={(e) => onSetSearch(e.target.value)}
        />
      </div>

      <div className="log-list">
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🗒️</div>
            <div className="empty-text">No entries yet. Log a call, email, note, or quote above.</div>
          </div>
        ) : filtered.map(r => r.kind === 'log'
          ? <LogRow
              key={`l_${r.log!.id}`} log={r.log!} customers={customers}
              role={user.role} currentUserName={user.name}
              onEdit={onEditLog} onDelete={onDeleteLog}
            />
          : <QuoteRowEntry
              key={`q_${r.quote!.id}`} quote={r.quote!}
              role={user.role} currentUserName={user.name}
              onEdit={onEditQuote}
              onSetStatus={onSetQuoteStatus}
              onDelete={onDeleteQuote}
            />
        )}
      </div>
    </>
  );
}

// ─── Row renderers ─────────────────────────────────────────────────────────────
function LogRow({ log, customers, role, currentUserName, onEdit, onDelete }: {
  log: LogEntry; customers: Customer[]; role: User['role']; currentUserName: string;
  onEdit: (l: LogEntry) => void; onDelete: (id: string) => void;
}) {
  const c = customers.find(c => c.id === log.customerId);
  const canMutate = role === 'admin' || (c ? c.amName === currentUserName : false);
  return (
    <div className={`log-entry ${log.type}`}>
      <div className="entry-icon">{LOG_ICON[log.type] ?? '📋'}</div>
      <div className="entry-body">
        <div className="entry-top">
          <span className={`entry-type ${log.type}`}>{log.type}</span>
          {c && <span className="entry-customer">{c.name}</span>}
        </div>
        <div className="entry-text">{log.notes}</div>
        <div className="entry-meta">
          <span className="entry-time">🕐 {formatTs(log.ts)}</span>
          <span className="entry-user">👤 {log.user}</span>
        </div>
      </div>
      <div className="entry-actions">
        {canMutate && <button className="entry-del-btn" onClick={() => onEdit(log)}>Edit</button>}
        {canMutate && <button className="entry-del-btn" onClick={() => onDelete(log.id)}>Delete</button>}
      </div>
    </div>
  );
}

function QuoteRowEntry({ quote, role, currentUserName, onEdit, onSetStatus, onDelete }: {
  quote: Quote; role: User['role']; currentUserName: string;
  onEdit: (q: Quote) => void;
  onSetStatus: (q: Quote, status: QuoteStatus) => void;
  onDelete: (q: Quote) => void;
}) {
  const canMutate = role === 'admin' || quote.customerAmName === currentUserName;
  // Reuse log-entry visual but tinted as a quote (note color side bar).
  return (
    <div className="log-entry note">
      <div className="entry-icon">💬</div>
      <div className="entry-body">
        <div className="entry-top">
          <span className="entry-type note">QUOTE</span>
          <span className="entry-customer">{quote.customerName}</span>
          <QuoteStatusBadge status={quote.status} />
          <span style={{ fontSize: 11, color: 'var(--accent2)', fontFamily: "'DM Mono', monospace" }}>
            Ref {quote.clarityRef}
          </span>
          <span style={{ fontSize: 12, fontWeight: 600, color: quote.revenue ? 'var(--call)' : 'var(--text-dim)' }}>
            {quote.revenue ? fmtCcy(quote.revenue) : '£0'}
          </span>
        </div>
        {quote.notes && <div className="entry-text">{quote.notes}</div>}
        <div className="entry-meta">
          <span className="entry-time">🕐 {formatTs(quote.createdTs)}</span>
          <span className="entry-user">👤 {quote.user}</span>
          {quote.decidedTs && (
            <span className="entry-user">decided {formatTs(quote.decidedTs)}</span>
          )}
        </div>
      </div>
      <div className="entry-actions">
        {canMutate && (
          <QuoteRowActions
            q={quote}
            onWin={() => onSetStatus(quote, 'won')}
            onLose={() => onSetStatus(quote, 'lost')}
            onReopen={() => onSetStatus(quote, 'open')}
            onEdit={() => onEdit(quote)}
            onDelete={() => onDelete(quote)}
          />
        )}
      </div>
    </div>
  );
}
